import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Award, Target, Activity, Calendar, TrendingUp, Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useCpdEnabled } from '@/contexts/TenantContext';

interface TeamMemberDetailModalProps {
  open: boolean;
  onClose: () => void;
  memberId: string;
  memberName: string;
  memberEmail: string;
}

export function TeamMemberDetailModal({ 
  open, 
  onClose, 
  memberId, 
  memberName, 
  memberEmail 
}: TeamMemberDetailModalProps) {
  const cpdEnabled = useCpdEnabled();
  // Fetch member's evaluation history
  const { data: evaluations = [] } = useQuery({
    queryKey: ['member-evaluations', memberId],
    queryFn: async () => {
      const { data } = await supabase
        .from('simulation_evaluations')
        .select('id, agent_name, overall_score, evaluated_at, is_submitted')
        .eq('user_id', memberId)
        .eq('is_submitted', true)
        .is('deleted_at', null)
        .order('evaluated_at', { ascending: false })
        .limit(10);
      return data || [];
    },
    enabled: open && !!memberId,
  });

  // Fetch member's badges
  const { data: badges = [] } = useQuery({
    queryKey: ['member-badges', memberId],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_badges')
        .select('*')
        .eq('user_id', memberId)
        .order('earned_at', { ascending: false });
      return data || [];
    },
    enabled: open && !!memberId,
  });

  // Fetch member's streak
  const { data: streak } = useQuery({
    queryKey: ['member-streak', memberId],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_streaks')
        .select('*')
        .eq('user_id', memberId)
        .single();
      return data;
    },
    enabled: open && !!memberId,
  });

  // Fetch CPD points
  const { data: cpdPoints = [] } = useQuery({
    queryKey: ['member-cpd', memberId],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_cpd_points')
        .select('points')
        .eq('user_id', memberId);
      return data || [];
    },
    enabled: cpdEnabled && open && !!memberId,
  });

  const totalCpd = cpdPoints.reduce((sum, p) => sum + (p.points || 0), 0);
  const avgScore = evaluations.length > 0 
    ? Math.round(evaluations.reduce((sum, e) => sum + (e.overall_score || 0), 0) / evaluations.length)
    : 0;
  const bestScore = evaluations.length > 0 
    ? Math.max(...evaluations.map(e => e.overall_score || 0))
    : 0;

  const initials = memberName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Team Member Details</DialogTitle>
          <DialogDescription className="sr-only">View team member profile, performance, and activity</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Profile Header */}
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src="" />
              <AvatarFallback className="bg-primary/10 text-primary text-xl font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-xl font-semibold">{memberName}</h3>
              <p className="text-muted-foreground">{memberEmail}</p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className={`grid grid-cols-2 gap-4 ${cpdEnabled ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
            <Card>
              <CardContent className="p-4 text-center">
                <Activity className="h-5 w-5 mx-auto mb-2 text-blue-500" />
                <p className="text-2xl font-bold">{evaluations.length}</p>
                <p className="text-xs text-muted-foreground">Sessions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Target className="h-5 w-5 mx-auto mb-2 text-green-500" />
                <p className="text-2xl font-bold">{avgScore}%</p>
                <p className="text-xs text-muted-foreground">Avg Score</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <TrendingUp className="h-5 w-5 mx-auto mb-2 text-purple-500" />
                <p className="text-2xl font-bold">{bestScore}%</p>
                <p className="text-xs text-muted-foreground">Best Score</p>
              </CardContent>
            </Card>
            {cpdEnabled ? (
              <Card>
                <CardContent className="p-4 text-center">
                  <Star className="h-5 w-5 mx-auto mb-2 text-yellow-500" />
                  <p className="text-2xl font-bold">{totalCpd}</p>
                  <p className="text-xs text-muted-foreground">CPD Points</p>
                </CardContent>
              </Card>
            ) : null}
          </div>

          {/* Current Streak */}
          {streak && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🔥</span>
                    <div>
                      <p className="font-semibold">{streak.current_streak || 0} Day Streak</p>
                      <p className="text-xs text-muted-foreground">
                        Longest: {streak.longest_streak || 0} days
                      </p>
                    </div>
                  </div>
                  {streak.last_activity_date && (
                    <p className="text-xs text-muted-foreground">
                      Last active: {new Date(streak.last_activity_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Badges */}
          {badges.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Award className="h-4 w-4" />
                Badges Earned ({badges.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {badges.map((badge) => (
                  <Badge key={badge.id} variant="secondary" className="px-3 py-1">
                    <span className="mr-1">{badge.badge_icon}</span>
                    {badge.badge_name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Recent Sessions */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Recent Sessions
            </h4>
            {evaluations.length === 0 ? (
              <p className="text-muted-foreground text-sm">No completed sessions yet</p>
            ) : (
              <div className="space-y-2">
                {evaluations.map((evaluation) => (
                  <div 
                    key={evaluation.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                  >
                    <div>
                      <p className="font-medium text-sm">{evaluation.agent_name || 'Unknown Agent'}</p>
                      <p className="text-xs text-muted-foreground">
                        {evaluation.evaluated_at 
                          ? new Date(evaluation.evaluated_at).toLocaleDateString()
                          : 'Unknown date'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Progress 
                        value={evaluation.overall_score || 0} 
                        className="w-20 h-2"
                      />
                      <Badge variant={evaluation.overall_score && evaluation.overall_score >= 80 ? 'default' : 'outline'}>
                        {evaluation.overall_score || 0}%
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
